import { z } from "zod";

/**
 * Shared DTO cho Admin CI/CD & Human-Gate oversight (US-AE10.F1-0033 · @UC9090 pipeline-gate-oversight
 * · @UC9091 i18n-gate-status). Một nguồn sự thật cho api (S01/S03) và admin web (S03/S04).
 *
 * INVARIANT CỨNG (AC4 — cả story xoay quanh điểm này): portal **CHỈ QUAN SÁT**. DTO KHÔNG có field/
 * action approve/merge gate — chỉ đọc trạng thái + deep-link `ciUrl` tới hệ CI thật. Reviewer + test
 * assert vắng route/action approve.
 *
 * Decision Record (PROGRESS.md): nguồn dữ liệu = bảng ghi provider-agnostic (`pipeline_runs` +
 * `human_gate_events`) do CI webhook/job feed (deferred). Read-API query bảng — KHÔNG gọi Bitbucket
 * trực tiếp. `ciUrl` deep-link derive từ env BITBUCKET_WORKSPACE/REPO_SLUG phía server (KHÔNG token
 * trong DTO — G4/AC6).
 *
 * BẤT BIẾN (invariants):
 * - AC4: read-only — KHÔNG DTO/field mutate gate/pipeline.
 * - G4: DTO KHÔNG mang token/secret CI (chỉ ciUrl công khai + metadata).
 * - `.strict()` mọi object — chống over-post/field lạ tại biên.
 */

// ============================================================
// Enum-lite (khớp badge STATUS admin — pipeline stage/gate)
// ============================================================

/** Tên stage pipeline (khớp story AC1: lint→unit→integration→eval→migration-check→deploy). */
export const pipelineStageNameSchema = z.enum([
  "lint",
  "unit",
  "integration",
  "eval",
  "migration-check",
  "deploy",
]);
export type PipelineStageName = z.infer<typeof pipelineStageNameSchema>;

/** Trạng thái 1 stage (khớp badge STATUS: passed/failed/running/skipped/pending). */
export const pipelineStageStatusSchema = z.enum([
  "passed",
  "failed",
  "running",
  "skipped",
  "pending",
]);
export type PipelineStageStatus = z.infer<typeof pipelineStageStatusSchema>;

/** Trạng thái tổng của 1 pipeline (rollup từ stages). */
export const pipelineOverallStatusSchema = z.enum(["passed", "failed", "running"]);
export type PipelineOverallStatus = z.infer<typeof pipelineOverallStatusSchema>;

/**
 * Loại @gate tự kích hoạt theo path-glob (story AC2). Feeder áp path-glob repo → ghi gateType; read-API
 * chỉ surface. migration/ledger/payments/rbac/config/ai (khớp vùng nhạy cảm SDLC).
 */
export const gateTypeSchema = z.enum([
  "migration",
  "ledger",
  "payments",
  "rbac",
  "config",
  "ai",
]);
export type GateType = z.infer<typeof gateTypeSchema>;

/** Trạng thái gate (story AC2/AC4: chờ duyệt hoặc đã duyệt ở hệ CI — portal chỉ đọc). */
export const gateStatusSchema = z.enum(["pending", "approved"]);
export type GateStatus = z.infer<typeof gateStatusSchema>;

/** Trạng thái cổng i18n (story AC3: pass hoặc fail khi thiếu key/hard-code). */
export const i18nGateStatusSchema = z.enum(["pass", "fail"]);
export type I18nGateStatus = z.infer<typeof i18nGateStatusSchema>;

// ============================================================
// @UC9090 — Pipeline oversight (READ)
// ============================================================

export const PIPELINE_MAX_LIMIT = 100;

/** Một stage pipeline: tên + status + deep-link CI optional (KHÔNG action). */
export const pipelineStageSchema = z
  .object({
    name: pipelineStageNameSchema,
    status: pipelineStageStatusSchema,
    /** Deep-link tới log stage trên CI (chỉ đọc). Null khi feeder chưa cung cấp. */
    url: z.string().nullable(),
  })
  .strict();
export type PipelineStage = z.infer<typeof pipelineStageSchema>;

/**
 * Trạng thái 1 pipeline (1 PR/commit). stages = mảng pass/fail mỗi bước. `ciUrl` deep-link (AC4 —
 * chỉ mở trên CI, KHÔNG approve trong portal).
 */
export const pipelineStatusSchema = z
  .object({
    id: z.string(),
    prNumber: z.number().int().nullable(),
    branch: z.string(),
    commit: z.string(),
    title: z.string(),
    overall: pipelineOverallStatusSchema,
    stages: z.array(pipelineStageSchema),
    ciUrl: z.string(),
    updatedAt: z.string(),
  })
  .strict();
export type PipelineStatusItem = z.infer<typeof pipelineStatusSchema>;

/** Envelope list pipeline `{ data, pageInfo }` (cursor pagination). */
export const pipelineListResponseSchema = z
  .object({
    data: z.array(pipelineStatusSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type PipelineListResponse = z.infer<typeof pipelineListResponseSchema>;

/** Query GET /admin/ci/pipelines — cursor + limit + filter overall status. */
export const pipelineListQuerySchema = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(PIPELINE_MAX_LIMIT).optional(),
    status: pipelineOverallStatusSchema.optional(),
  })
  .strict();
export type PipelineListQuery = z.infer<typeof pipelineListQuerySchema>;

// ============================================================
// @UC9090 — Human gate oversight (READ) + @UC9091 i18n gate
// ============================================================

/**
 * Một @gate cần duyệt (AC2). `requiredApprovers` = ai được phép duyệt (feeder ghi từ config repo).
 * `author` = tác giả PR → UI/test kiểm approver≠author. `ciUrl` deep-link (AC4 — duyệt trên CI, KHÔNG
 * ở portal). KHÔNG field approve/action.
 */
export const pendingGateSchema = z
  .object({
    id: z.string(),
    prNumber: z.number().int().nullable(),
    branch: z.string(),
    gateType: gateTypeSchema,
    status: gateStatusSchema,
    /** Danh sách người được phép duyệt (approver hợp lệ). KHÔNG rỗng khi status=pending. */
    requiredApprovers: z.array(z.string()),
    /** Tác giả PR — để UI cảnh báo approver=author (không được tự duyệt). */
    author: z.string(),
    ciUrl: z.string(),
    createdAt: z.string(),
  })
  .strict();
export type PendingGate = z.infer<typeof pendingGateSchema>;

/** Một key thiếu bản dịch (AC3): key + locale thiếu. */
export const i18nMissingKeySchema = z
  .object({
    key: z.string(),
    locale: z.string(),
  })
  .strict();
export type I18nMissingKey = z.infer<typeof i18nMissingKeySchema>;

/** Một chuỗi hard-code (AC3): file + line. */
export const i18nHardcodedStringSchema = z
  .object({
    file: z.string(),
    line: z.number().int().min(0),
  })
  .strict();
export type I18nHardcodedString = z.infer<typeof i18nHardcodedStringSchema>;

/**
 * Báo cáo cổng i18n (AC3 · E15-13). status=fail khi có missingKeys/hardcodedStrings. Read-only —
 * portal chỉ hiển thị key report; sửa ở PR/repo. checkedAt null khi feeder chưa chạy job i18n.
 */
export const i18nGateReportSchema = z
  .object({
    status: i18nGateStatusSchema,
    missingKeys: z.array(i18nMissingKeySchema),
    hardcodedStrings: z.array(i18nHardcodedStringSchema),
    checkedAt: z.string().nullable(),
  })
  .strict();
export type I18nGateReport = z.infer<typeof i18nGateReportSchema>;

/**
 * Response GET /admin/ci/gates (@UC9090 AC2 + @UC9091 AC3): pending gate list + i18n gate report.
 * KHÔNG action approve/merge (AC4). Gate list có cursor (pendingGates dài).
 */
export const gateOversightResponseSchema = z
  .object({
    pendingGates: z.array(pendingGateSchema),
    i18nGate: i18nGateReportSchema,
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type GateOversightResponse = z.infer<typeof gateOversightResponseSchema>;

/** Query GET /admin/ci/gates — cursor + limit + filter gateType/status. */
export const gateListQuerySchema = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(PIPELINE_MAX_LIMIT).optional(),
    gateType: gateTypeSchema.optional(),
    status: gateStatusSchema.optional(),
  })
  .strict();
export type GateListQuery = z.infer<typeof gateListQuerySchema>;
